// Implements: F-UIX-018, F-USG-005, F-USG-006, F-UIX-010
/**
 * "Mi consumo" (UIX.4): the signed-in person's own month, the period's consumption and the
 * per-mockup cost list, from three self-only reads.
 *
 * Each section has its own loading, error and empty state (F-UIX-010): a failed list does
 * not hide the month, and its retry asks only for what failed. The person comes from the
 * session and keys every read (`useMyUsage`), and the page mounts this container keyed by
 * that same id (checklist 14), so nothing of one person survives on another's screen.
 * Nothing here touches browser storage. Each mockup row opens its cost by version (mockup v3 P7,
 * `VersionCostBreakdownContainer`, keyed by mockup). The page closes with the exports of its two
 * reports (owner 2026-10-19, `MyUsageExportsContainer`), which read nothing until asked.
 */

import type { UseQueryResult } from "@tanstack/react-query";
import type { ReactElement } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import { useTranslation } from "../../../shared/i18n";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import type { BackofficeScope } from "../../../types/api";
import { MyMockupCosts } from "../components/MyMockupCosts";
import { MyUsageMonth } from "../components/MyUsageMonth";
import { MyUsagePeriod } from "../components/MyUsagePeriod";
import styles from "../components/MyUsage.module.css";
import { useMyUsage } from "../hooks/useMyUsage";
import { calendarDayText } from "../model/myUsage";
import { MyUsageExportsContainer } from "./MyUsageExportsContainer";
import { VersionCostBreakdownContainer } from "./VersionCostBreakdownContainer";

export interface MyUsageContainerProps {
  /** The signed-in person, from the session. */
  readonly personId: string;
  /** The session's scopes, to name each organization the month lists. */
  readonly scopes: readonly BackofficeScope[];
}

/** One section's read: the loading state, the error state with its retry, or `render`. */
function Section<T>({
  read,
  render,
}: {
  readonly read: UseQueryResult<T, ApiError>;
  readonly render: (data: T) => ReactElement;
}): ReactElement {
  const { t } = useTranslation();
  if (read.isError) {
    return (
      <ErrorState
        error={asApiError(read.error)}
        onRetry={() => {
          void read.refetch();
        }}
      />
    );
  }
  if (read.data === undefined) return <LoadingState label={t("backoffice.myUsage.loading")} />;
  return render(read.data);
}

export function MyUsageContainer({ personId, scopes }: MyUsageContainerProps): ReactElement {
  const { t } = useTranslation();
  const { summary, consumption, mockups } = useMyUsage(personId);

  return (
    <div className={styles.section}>
      <Section read={summary} render={(data) => <MyUsageMonth summary={data} scopes={scopes} />} />
      <Section read={consumption} render={(data) => <MyUsagePeriod consumption={data} />} />
      <Section
        read={mockups}
        render={(data) => (
          <MyMockupCosts
            mockups={data}
            rangeText={t("backoffice.myUsage.period.range", {
              from: calendarDayText(data.from),
              to: calendarDayText(data.to),
            })}
            renderBreakdown={(mockup) => (
              <VersionCostBreakdownContainer key={mockup.id} personId={personId} mockupId={mockup.id} name={mockup.name} />
            )}
          />
        )}
      />
      <MyUsageExportsContainer />
    </div>
  );
}
